import { useEffect, useId, useMemo, useState } from 'react'

import { Controller, UseFormReturn } from 'react-hook-form'

import { DateTimePicker } from '@tribeplatform/react-ui-kit/DateTimePicker'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { RadioGroup } from '@tribeplatform/react-ui-kit/RadioGroup'
import { Text } from '@tribeplatform/react-ui-kit/Text'

import { T, useI18n } from '../../i18n/index.js'

import { PollFormValues } from './types.js'

type ClosesType = 'never' | 'schedule' | 'closed'

export function PollFormClosesAt({
  form,
}: {
  form: UseFormReturn<PollFormValues>
}) {
  const id = useId()
  const { $t } = useI18n()

  const [closesType, setClosesType] = useState<ClosesType>(() => {
    const closesAt = form.getValues('closesAt')

    if (!closesAt) {
      return 'never'
    }

    if (closesAt > new Date()) {
      return 'schedule'
    }

    return 'closed'
  })

  const opensAt = form.getValues('opensAt')
  const minDate = useMemo(() => {
    if (!opensAt) {
      return new Date()
    }
    if (opensAt > new Date()) {
      return opensAt
    }
    return new Date()
  }, [opensAt])

  useEffect(() => {
    if (closesType === 'never') {
      form.setValue('closesAt', null, { shouldDirty: true })
    } else {
      form.resetField('closesAt')
    }
  }, [closesType, form])

  return (
    <div className="space-y-4">
      <FormControl.Label htmlFor={`poll-selection-type-${id}`}>
        <div className="flex flex-col">
          <Text variant="labelLg">
            <T id="Poll.Form.ClosesAt.Title" defaultMessage="Voting deadline" />
          </Text>
          <Text className="text-content-muted" variant="bodySm">
            <T
              id="Poll.Form.ClosesAt.Description"
              defaultMessage="Set a closing time for when voting ends"
            />
          </Text>
        </div>
      </FormControl.Label>
      <FormControl.RadioGroup
        id={`poll-selection-type-${id}`}
        name="closesType"
        value={closesType}
        onChange={value => {
          setClosesType(value as ClosesType)
        }}
      >
        <RadioGroup.Items>
          <RadioGroup.Item
            value="never"
            title={$t({
              id: 'Poll.Form.ClosesAt.Never',
              defaultMessage: 'No deadline',
            })}
          />
          <RadioGroup.Item
            value="schedule"
            title={$t({
              id: 'Poll.Form.ClosesAt.Schedule',
              defaultMessage: 'Set closing time',
            })}
          />
        </RadioGroup.Items>
      </FormControl.RadioGroup>
      {closesType === 'schedule' && (
        <Controller
          control={form.control}
          name="closesAt"
          render={({ field }) => {
            return (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <FormControl.Label htmlFor={`closes-at-${id}`}>
                    <T
                      id="Poll.Form.ClosesAt.ScheduleLabel"
                      defaultMessage="Voting ends at"
                    />
                  </FormControl.Label>
                </div>
                <DateTimePicker
                  id={`closes-at-${id}`}
                  minDate={minDate}
                  value={field.value}
                  onChange={field.onChange}
                />
              </div>
            )
          }}
        />
      )}
    </div>
  )
}
